import React from 'react'
import Joi from 'joi'
import { t } from 'i18next'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, BusinessOutlined, ContentCopy, KeyRounded, WarningAmberRounded } from '@mui/icons-material'
import { useDispatch } from '../../store'
import { createEnterpriseUser } from '../../store/user/actionCreator'
import { CreateEnterpriseUserResult } from '../../store/user/actionType'
import { ErrorsState, notRequired, preventCyrillicOnInput, regex, validateForm } from '../../function/validation'
import l from '../../page/SubscribersPageV2.module.css'
import s from './EnterpriseV2.module.css'

/*
 * New Enterprise user — the classic PopupCreateEnterpriseUser's fields and
 * rules (all optional; names in Latin letters), in the v2 look. The backend
 * answers with the access key the user signs in with; it is shown once
 * here, with a copy button and the reminder to pass it on now.
 */

const optionalLatinOnly = Joi.string().regex(regex.latinOnly).allow('').messages({ 'string.pattern.base': t('message.error.latinOnly') })
const schema = Joi.object({
  firstname: optionalLatinOnly.label('First name'),
  lastname: optionalLatinOnly.label('Last name'),
  companyName: notRequired.label('Company'),
})

export function CreateEnterpriseDialogV2({ theme, companies, onClose, onCreated }: {
  theme: string
  companies: string[]
  onClose: () => void
  onCreated: () => void
}) {
  const dispatch = useDispatch()
  const [firstname, setFirstname] = React.useState('')
  const [lastname, setLastname] = React.useState('')
  const [companyName, setCompanyName] = React.useState('')
  const [errors, setErrors] = React.useState<ErrorsState>(null)
  const [creating, setCreating] = React.useState(false)
  const [result, setResult] = React.useState<CreateEnterpriseUserResult | null>(null)
  const [copied, setCopied] = React.useState(false)

  function create() {
    const error = validateForm(schema, { firstname, lastname, companyName })
    if (error) { setErrors(error); return }
    setErrors(null)
    setCreating(true)
    dispatch(createEnterpriseUser(
      { firstname: firstname || undefined, lastname: lastname || undefined, companyName: companyName.trim() || undefined },
      (data) => { setCreating(false); setResult(data); onCreated() },
      () => setCreating(false),
    ))
  }

  const err = (k: string) => (errors as Record<string, string> | null)?.[k]
  const matches = companyName.trim()
    ? companies.filter((c) => c.toLowerCase().includes(companyName.trim().toLowerCase()) && c !== companyName.trim()).slice(0, 6)
    : companies.slice(0, 6)

  return (
    <Dialog open onClose={creating ? undefined : onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2 } }}>
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={s.topIcon}>{result ? <KeyRounded sx={{ fontSize: 19 }} /> : <BusinessOutlined sx={{ fontSize: 19 }} />}</span>
          <div className={s.topText}>
            <b>{result ? 'Enterprise user created' : 'New Enterprise user'}</b>
            <span>{result ? 'They sign in with this access key — no email or password' : 'Signs in with an access key instead of email or phone'}</span>
          </div>
          <button type="button" className={s.close} onClick={onClose} disabled={creating} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        {result ? (
          <div className={s.form}>
            <div className={s.warn}><WarningAmberRounded sx={{ fontSize: 18 }} />Copy the access key now and pass it on safely.</div>
            <div className={s.field}>
              <span className={s.label}>Access key</span>
              <div className={s.keyBox}>
                <code>{result.accessKey}</code>
                <button
                  type="button"
                  className={s.ghostSm}
                  onClick={() => { void navigator.clipboard?.writeText(result.accessKey).catch(() => undefined); setCopied(true) }}
                >
                  <ContentCopy sx={{ fontSize: 14 }} />{copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div className={s.field}>
              <span className={s.label}>User ID</span>
              <code className={s.mono}>{result.uid}</code>
            </div>
            <div className={s.foot}>
              <span className={s.footNote}>{copied ? 'Copied to the clipboard' : 'Not copied yet'}</span>
              <button type="button" className={s.primary} onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <form className={s.form} onSubmit={(e) => { e.preventDefault(); create() }} noValidate>
            <div className={s.grid2}>
              <label className={s.field}>
                <span className={s.label}>First name <em>optional</em></span>
                <input className={s.input} value={firstname} maxLength={50} autoFocus onChange={(e) => preventCyrillicOnInput(e, setFirstname)} />
                {err('firstname') && <span className={s.err}>{err('firstname')}</span>}
              </label>
              <label className={s.field}>
                <span className={s.label}>Last name <em>optional</em></span>
                <input className={s.input} value={lastname} maxLength={50} onChange={(e) => preventCyrillicOnInput(e, setLastname)} />
                {err('lastname') && <span className={s.err}>{err('lastname')}</span>}
              </label>
            </div>
            <div className={s.field}>
              <label className={s.field}>
                <span className={s.label}>Company <em>optional</em></span>
                <input className={s.input} value={companyName} maxLength={120} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acme Ltd" />
              </label>
              {matches.length > 0 && (
                <div className={s.suggest}>
                  <span>Existing:</span>
                  {matches.map((c) => <button key={c} type="button" className={l.chip} onClick={() => setCompanyName(c)}>{c}</button>)}
                </div>
              )}
            </div>
            <div className={s.foot}>
              <span className={s.footNote}>The access key is made by the server</span>
              <button type="button" className={s.ghost} onClick={onClose} disabled={creating}>Cancel</button>
              <button type="submit" className={s.primary} disabled={creating}><KeyRounded sx={{ fontSize: 16 }} />{creating ? 'Creating…' : 'Create & get key'}</button>
            </div>
          </form>
        )}
      </div>
    </Dialog>
  )
}
